<template>
	<div class="assessment-prompt" :data-person-id="user.personId">
		<header class="assessment-prompt__header">
			<div class="assessment-prompt__header-segment--left">
				<div class="assessment-prompt__user-heading">
					<UserAvatar
						:user="user"
						size="3.5rem"
						class-name="assessment-prompt__avatar"
					/>
					<div class="assessment-prompt__user-heading-title">
						<div>
							<h1 class="assessment-prompt__user-name-heading">{{ user.displayName }}</h1>
							<FontAwesomeIcon
								:icon="[ 'fas', 'question-circle', ]"
								class="assessment-prompt__question-icon"
							/>
						</div>
						<small class="assessment-prompt__user-name-heading-link">
							{{ $t( "common.AssessmentPrompt__headerLinkDescription" ) }}
							<a href="http://help.teaming.com/en/articles/6015460-understanding-teaming-s-disc-work-styles"
								:title="$t( `common.AssessmentPrompt__headerLinkText` )"
								target="_blank"
							>{{ $t( "common.AssessmentPrompt__headerLinkText" ) }}</a>
						</small>
					</div>
				</div>
				<div class="assessment-prompt__description">
					<p v-if="isCurrentUser">{{ $t( "common.AssessmentPrompt_thisUserDescription1" ) }}</p>
					<p v-if="isCurrentUser">{{ $t( "common.AssessmentPrompt_thisUserDescription2" ) }}</p>
					<p v-if="!isCurrentUser && user.shortDisplayName">{{ $t( "common.AssessmentPrompt_otherUserDescription1a", { name: user.shortDisplayName, } ) }}</p>
					<p v-if="!isCurrentUser && !user.shortDisplayName">{{ $t( "common.AssessmentPrompt_otherUserDescription1b" ) }}</p>
					<div class="assessment-prompt__buttons" v-if="isCurrentUser">
						<TeamingButton
							@click="$emit( 'prompt', 'assessment' )"
						>{{ $t( "common.AssessmentPrompt_takeAssessmentButton" ) }}</TeamingButton>
					</div>
					<div class="assessment-prompt__buttons" v-else-if="!( isCurrentUser || user.workstyleReminderSent )">
						<TeamingButton
							v-if="user.shortDisplayName"
							@click="$emit( 'prompt', 'remind' )"
						>{{ $t( "common.AssessmentPrompt_remindButton1", { name: user.shortDisplayName } ) }}</TeamingButton>
						<TeamingButton
							v-else
							@click="$emit( 'prompt', 'remind' )"
						>{{ $t( "common.AssessmentPrompt_remindButton2" ) }}</TeamingButton>
					</div>
					<div class="assessment-prompt__buttons" v-if="user.workstyleReminderSent">
						<!-- empty as intended -->
						<TeamingAlert>{{ $t( "common.AssessmentPrompt_reminderConfirmation", { name: user.shortDisplayName } ) }}</TeamingAlert>
					</div>
					<div class="assessment-prompt__buttons" v-if="error">
						<TeamingAlert type="ERROR">{{ error }}</TeamingAlert>
					</div>
				</div>
			</div>
			<div
				v-if="!isCurrentUser"
				class="assessment-prompt__header-segment--right"
			>
				<DISCWheel class="assessment-prompt__disc-wheel" />
			</div>
		</header>
		<footer class="assessment-prompt__skeletons">
			<AssessmentCharacteristics>
				<template #icon>
					<FontAwesomeIcon
						:icon="['fad', 'head-side-brain']"
					/>
				</template>
				<template #heading>
					<slot name="heading1">
						<span v-if="!isCurrentUser && user.shortDisplayName">
							{{ $t( "common.AssessmentPrompt_theirSkeletonHeading1a", { name: user.shortDisplayName } ) }}
						</span>
						<span v-else-if="!isCurrentUser && !user.shortDisplayName">
							{{ $t( "common.AssessmentPrompt_theirSkeletonHeading1b" ) }}
						</span>
						<span v-else>
							{{ $t( "common.AssessmentPrompt_yourSkeletonHeading1" ) }}
						</span>
					</slot>
				</template>
			</AssessmentCharacteristics>
			<AssessmentCharacteristics>
				<template #icon>
					<FontAwesomeIcon
						:icon="['fad', 'chevron-double-up']"
					/>
				</template>
				<template #heading>
					<slot name="heading2">
						<span v-if="!isCurrentUser && user.shortDisplayName">
							{{ $t( "common.AssessmentPrompt_theirSkeletonHeading2a", { name: user.shortDisplayName } ) }}
						</span>
						<span v-else-if="!isCurrentUser && !user.shortDisplayName">
							{{ $t( "common.AssessmentPrompt_theirSkeletonHeading2b" ) }}
						</span>
						<span v-else>
							{{ $t( "common.AssessmentPrompt_yourSkeletonHeading2" ) }}
						</span>
					</slot>
				</template>
			</AssessmentCharacteristics>
			<AssessmentCharacteristics>
				<template #icon>
					<FontAwesomeIcon
						:icon="['fad', 'chevron-double-down']"
					/>
				</template>
				<template #heading>
					<slot name="heading3">
						<span v-if="!isCurrentUser && user.shortDisplayName">
							{{ $t( "common.AssessmentPrompt_theirSkeletonHeading3a", { name: user.shortDisplayName } ) }}
						</span>
						<span v-else-if="!isCurrentUser && !user.shortDisplayName">
							{{ $t( "common.AssessmentPrompt_theirSkeletonHeading3b" ) }}
						</span>
						<span v-else>
							{{ $t( "common.AssessmentPrompt_yourSkeletonHeading3" ) }}
						</span>
					</slot>
				</template>
			</AssessmentCharacteristics>
		</footer>
	</div>
</template>

<script>
import DISCWheel from "./DISCWheel";
import TeamingAlert from "../TeamingAlert.vue";
import TeamingButton from "../buttons/TeamingButton.vue";
import UserAvatar from "../UserAvatar.vue";
import AssessmentCharacteristics from "./characteristics/AssessmentCharacteristics";

export default {
	name: "AssessmentPrompt",
	emits: [
		"prompt",
	],
	components: {
		TeamingButton,
		UserAvatar,
		DISCWheel,
		TeamingAlert,
		AssessmentCharacteristics,
	},
	props: {
		user: {
			type: Object,
			required: true,
		},
		isCurrentUser: {
			type: Boolean,
			required: true,
		},
		error: {
			type: String,
			required: true,
		},
	},
};
</script>

<style lang="scss" scoped>
.assessment-prompt__user-heading {
	display: flex;
	flex-direction: row;
	justify-content: flex-start;
	align-items: center;
}

.assessment-prompt__user-heading-title {
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	align-items: flex-start;
	gap: 0.5rem;

	div:first-child {
		display: flex;
		flex-direction: row;
		justify-content: flex-start;
		align-items: center;
	}
}

.assessment-prompt__user-name-heading-link {
	display: block;
	padding-left: 1rem;
	@include font-and-line-for(-1);
	text-align: left;
	color: $assessment-prompt-user-name-heading-link-text-color;

	a, a:hover, a:visited {
		color: $assessment-prompt-user-name-heading-link-anchor-color;
	}
}

.assessment-prompt__avatar {
	margin: 0;
}

.assessment-prompt__user-name-heading {
	@include font-and-line-for(1);
	color: $assessment-prompt-heading-color;
	padding: 0 1rem;
}

.assessment-prompt__question-icon {
	font-size: 1.5rem;
	color: $assessment-prompt-question-icon-color;
	margin: 0;
}

.assessment-prompt__description {
	text-align: left;
	margin: 2rem 0 0 0;

	p {
		margin: 1rem 0;
		@include font-and-line-for(0);
		color: $assessment-prompt-paragraph-color;
	}

	p:first-of-type {
		margin-top: 0;
	}

	p:last-of-type {
		margin-bottom: 0;
	}
}

.assessment-prompt__disc-wheel {
	max-height: 15rem;
}

.assessment-prompt__buttons {
	margin-top: 1.5rem;
	display: flex;
	flex-direction: row;
	justify-content: center;

	@include for-size('sm') {
		justify-content: flex-start;
	}
}

.assessment-prompt__skeletons {
	display: flex;
	flex-direction: column;
	gap: 1rem;

	margin: 1.5rem 0 1rem 0;
}

.assessment-prompt__header {
	display: flex;
	flex-direction: column;
	justify-content: flex-start;

	@include for-size('sm') {
		flex-direction: row;
	}
}

.assessment-prompt__header-segment--left {

	@include for-size('sm') {
		flex: 3;
	}
}

.assessment-prompt__header-segment--right {
	margin: 2rem 0 0 0;
	padding: 0 2rem;

	@include for-size('sm') {
		flex: 1;
		margin-top: 0;
	}
}
</style>
